import axios, { AxiosError } from 'axios';
import { BASE_URL, SEASON_API_PATH } from '../../../constants/api';
import { SeasonInterface, SeasonData, SeasonApiRequest } from '../../type/seasonType';
import { FetchResult } from '../../type/apiType';
import { I18nApiErrorHandler } from '../../services/i18n/i18nApiErrorHandler';
import { ApiService } from './apiService';

class SeasonApiService extends ApiService<SeasonInterface> {
  constructor() {
    super(SEASON_API_PATH);
  }

  /**
   * i18n 필드를 포함한 Season 목록 조회
   */
  public async fetchSeasonListWithI18n(
    queryParams?: URLSearchParams
  ): Promise<FetchResult<SeasonData>> {
    try {
      const params = new URLSearchParams(queryParams);

      // i18n 필드를 포함한 필드 목록 설정
      const i18nFields = [
        'id',
        'series_id',
        'season_number',
        'slug',
        'status',
        'created',
        'modified',
        'dubbing_mode',
        'season_order',
        'age_rating',
        'release_date_start',
        'release_date_end',
        'cast',
        'genres',
        'series',
        'contents',
        'categories',
        'is_deletable',
        'title',
        'title_en',
        'synopsis',
        'synopsis_en',
        'seo_title',
        'seo_synopsis',
        'seo_keyword',
        'i18n_title',
        'i18n_synopsis',
        'i18n_seo_title',
        'i18n_seo_synopsis',
        'i18n_seo_keyword',
      ].join(',');
      params.set('fields', i18nFields);

      const response = await axios.get(`${BASE_URL}${SEASON_API_PATH}`, {
        params: Object.fromEntries(params),
      });
      return response.data;
    } catch (error) {
      const i18nErrors = I18nApiErrorHandler.handleAxiosError(error as AxiosError);
      throw new Error(I18nApiErrorHandler.formatErrorsForUser(i18nErrors));
    }
  }

  /**
   * i18n 필드를 포함한 단일 Season 조회
   */
  public async fetchSeasonWithI18n(id: number): Promise<SeasonData> {
    try {
      const fields = [
        'id',
        'series_id',
        'season_number',
        'slug',
        'status',
        'created',
        'modified',
        'dubbing_mode',
        'season_order',
        'age_rating',
        'release_date_start',
        'release_date_end',
        'cast',
        'genres',
        'series',
        'contents',
        'categories',
        'is_deletable',
        'title',
        'title_en',
        'synopsis',
        'synopsis_en',
        'seo_title',
        'seo_synopsis',
        'seo_keyword',
        'i18n_title',
        'i18n_synopsis',
        'i18n_seo_title',
        'i18n_seo_synopsis',
        'i18n_seo_keyword',
      ].join(',');

      const params = { fields };
      const response = await axios.get(`${BASE_URL}${SEASON_API_PATH}${id}/`, { params });
      return response.data;
    } catch (error) {
      const i18nErrors = I18nApiErrorHandler.handleAxiosError(error as AxiosError);
      throw new Error(I18nApiErrorHandler.formatErrorsForUser(i18nErrors));
    }
  }

  /**
   * 레거시 필드와 i18n 필드를 모두 포함한 Season 생성
   */
  public async createSeasonWithI18n(seasonData: SeasonApiRequest): Promise<SeasonData> {
    try {
      const response = await axios.post(`${BASE_URL}${SEASON_API_PATH}`, seasonData);
      return response.data;
    } catch (error) {
      const i18nErrors = I18nApiErrorHandler.handleAxiosError(error as AxiosError);
      throw new Error(I18nApiErrorHandler.formatErrorsForUser(i18nErrors));
    }
  }

  /**
   * 레거시 필드와 i18n 필드를 모두 포함한 Season 업데이트
   */
  public async updateSeasonWithI18n(
    id: number,
    seasonData: Partial<SeasonApiRequest>
  ): Promise<SeasonData> {
    try {
      const response = await axios.patch(`${BASE_URL}${SEASON_API_PATH}${id}/`, seasonData);
      return response.data;
    } catch (error) {
      const i18nErrors = I18nApiErrorHandler.handleAxiosError(error as AxiosError);
      throw new Error(I18nApiErrorHandler.formatErrorsForUser(i18nErrors));
    }
  }

  /**
   * 특정 언어의 Season 목록 조회 (언어별 필터링)
   */
  public async fetchSeasonsByLanguage(
    language: string,
    queryParams?: URLSearchParams
  ): Promise<FetchResult<SeasonData>> {
    try {
      const params = new URLSearchParams(queryParams);

      // 특정 언어의 번역이 있는 Season만 필터링
      params.set('has_translation', language);

      return this.fetchSeasonListWithI18n(params);
    } catch (error) {
      const i18nErrors = I18nApiErrorHandler.handleAxiosError(error as AxiosError);
      throw new Error(I18nApiErrorHandler.formatErrorsForUser(i18nErrors));
    }
  }

  /**
   * 번역 완성도별 Season 조회
   */
  public async fetchSeasonsByTranslationStatus(
    requiredLanguages: string[] = ['ko'],
    queryParams?: URLSearchParams
  ): Promise<FetchResult<SeasonData>> {
    try {
      const params = new URLSearchParams(queryParams);

      // 필수 언어 번역 상태 필터
      params.set('required_languages', requiredLanguages.join(','));

      return this.fetchSeasonListWithI18n(params);
    } catch (error) {
      const i18nErrors = I18nApiErrorHandler.handleAxiosError(error as AxiosError);
      throw new Error(I18nApiErrorHandler.formatErrorsForUser(i18nErrors));
    }
  }

  /**
   * 특정 Series에 속한 Season 목록 조회 (i18n 지원)
   */
  public async fetchSeasonsBySeriesWithI18n(
    seriesId: number,
    queryParams?: URLSearchParams
  ): Promise<FetchResult<SeasonData>> {
    try {
      const params = new URLSearchParams(queryParams);
      params.set('series_id', seriesId.toString());

      return this.fetchSeasonListWithI18n(params);
    } catch (error) {
      const i18nErrors = I18nApiErrorHandler.handleAxiosError(error as AxiosError);
      throw new Error(I18nApiErrorHandler.formatErrorsForUser(i18nErrors));
    }
  }
}

export const seasonApiService = new SeasonApiService();
